// Setup-page Odia guides + completion status helpers.
//
// Each setup tab gets a friendly Odia explainer card (what to do, why, how
// long it takes), a status chip (✅ Complete / ⏳ Pending), and a "next
// step" hand-off toast when the user finishes a tab.
//
// Keep the copy short and concrete — teachers read this on small phones.

export const SETUP_TABS = ["schools", "vendors", "cchs", "enrollment"];

export const TAB_LABELS = {
  schools: { en: "School", od: "ବିଦ୍ୟାଳୟ" },
  vendors: { en: "Vendors", od: "ବ୍ୟବସାୟୀ" },
  cchs: { en: "Cook-cum-Helpers", od: "ରନ୍ଧାଳି (CCH)" },
  enrollment: { en: "Year-wise Enrolment", od: "ବର୍ଷୱାରୀ ନାମଲେଖା" },
};

// Friendly Odia guidance for each tab. Each guide has:
//   title       — short Odia heading
//   why         — one-line "why we need this"
//   steps       — bullet list of what to do (Odia)
//   timeMin     — estimated minutes to finish
//   completeMsg — toast Odia message shown the moment this tab flips to
//                 complete; embeds the NEXT tab's name.
export const TAB_GUIDES = {
  schools: {
    title: "📍 ବିଦ୍ୟାଳୟ ବିବରଣୀ ପୂରଣ କରନ୍ତୁ",
    why: "ସମସ୍ତ Quarterly UC, Hand Receipt ଏବଂ MDCF ରେ ଏହି ତଥ୍ୟ ସ୍ୱୟଂଚାଳିତ ଭାବେ ଆସିଯିବ।",
    steps: [
      "School Name ଓ UDISE Code (ବାଧ୍ୟତାମୂଳକ)",
      "District, Block, Village, Post, Thana, Pincode — ଇଂରାଜୀ ଓ ଓଡ଼ିଆ ଉଭୟ ରେ",
      "ପ୍ରତି class ର ନାମଲେଖା ସଂଖ୍ୟା (Bal Vatika / Primary / Upper Primary / High School)",
    ],
    timeMin: 5,
    completeMsg: "✅ ବିଦ୍ୟାଳୟ ବିବରଣୀ Complete! ଏବେ <b>Vendors (ବ୍ୟବସାୟୀ)</b> tab ରେ ଯାଇ ବ୍ୟବସାୟୀ ତଥ୍ୟ ଯୋଗ କରନ୍ତୁ।",
  },
  vendors: {
    title: "🛒 ବ୍ୟବସାୟୀ ତାଲିକା ଯୋଗ କରନ୍ତୁ",
    why: "Hand Receipt, Voucher ଓ Bill ଗୁଡ଼ିକ ଏହି Vendor ତଥ୍ୟ ସହିତ ସ୍ୱୟଂଚାଳିତ ଭାବେ ତିଆରି ହେବ।",
    steps: [
      "ପ୍ରତ୍ୟେକ Category ପାଇଁ ବ୍ୟବସାୟୀ ଯୋଡନ୍ତୁ — Grocery, Vegetable, Egg, Fuel, Ladoo, Other",
      "ସମସ୍ତ ଖାନା ଇଂରାଜୀ ଓ ଓଡ଼ିଆ ଉଭୟ ଭାଷାରେ ପୂରଣ କରନ୍ତୁ (Name, Father, Village, Post, etc.)",
      "ଯଦି ସମସ୍ତ category ର ବ୍ୟବସାୟୀ ଏକା ବ୍ୟକ୍ତି, ତେବେ <b>Add Vendor</b> ବଟନ୍ ଦବାଇବା ସମୟରେ Yes ବାଛନ୍ତୁ — ସବୁ category ସ୍ୱୟଂ save ହୋଇଯିବ।",
    ],
    timeMin: 8,
    completeMsg: "✅ Vendors Complete! ଏବେ <b>Cook-cum-Helpers (ରନ୍ଧାଳି)</b> tab ରେ ଯାଇ ରନ୍ଧାଳି ତଥ୍ୟ ଯୋଗ କରନ୍ତୁ।",
  },
  cchs: {
    title: "👩‍🍳 Cook-cum-Helper (ରନ୍ଧାଳି) ତଥ୍ୟ ଯୋଡନ୍ତୁ",
    why: "CCH ର Honorarium / Payment Voucher ଓ Bank ଦେୟ ସ୍ୱୟଂଚାଳିତ ଭାବେ ଏଠାରୁ ଆସିଯିବ।",
    steps: [
      "ପ୍ରତ୍ୟେକ CCH ର Name, Father, Village (ଓଡ଼ିଆ ଓ ଇଂରାଜୀ)",
      "Gender, Category (SC / ST / OBC / GEN), Phone Number",
      "Bank Account ଓ IFSC (Optional — ଗୋପନୀୟ ଭାବେ store ହେବ)",
      "Monthly Honorarium ଓ Default Payment Mode (Bank / Cash / UPI)",
    ],
    timeMin: 5,
    completeMsg: "✅ CCH ତଥ୍ୟ Complete! ଏବେ <b>Year-wise Enrolment (ବର୍ଷୱାରୀ ନାମଲେଖା)</b> tab ରେ ଯାଇ ବର୍ଷ ଅନୁଯାୟୀ ନାମଲେଖା ସଂଖ୍ୟା save କରନ୍ତୁ।",
  },
  enrollment: {
    title: "📊 ବର୍ଷୱାରୀ ନାମଲେଖା save କରନ୍ତୁ",
    why: "ଗତ ବର୍ଷ ଓ ଚାଲୁବର୍ଷର ନାମଲେଖା ଡାଟା Daily Entry ଓ MDCF ସମୟରେ ଲାଗିବ।",
    steps: [
      "Academic Year ବାଛନ୍ତୁ (ଯଥା 2024-25)",
      "ପ୍ରତି class ର ନାମଲେଖା ସଂଖ୍ୟା ଲେଖନ୍ତୁ",
      "Save କରନ୍ତୁ — ପ୍ରତି ବର୍ଷ separately save କରନ୍ତୁ",
    ],
    timeMin: 3,
    completeMsg: "🎉 ସମ୍ପୂର୍ଣ୍ଣ Setup ସମାପ୍ତ! ଏବେ ଆପଣ Daily Entry, Quarterly Reports ଓ MDCF ବ୍ୟବହାର କରିପାରିବେ।",
  },
};

// Determine completion of each tab from the in-memory data state.
//   profile: the active school profile doc
//   vendors:  array of vendor docs
//   cchs:     array of cch docs
//   enrolls:  array of enrolment docs (or boolean — fallback)
//
// When `profile.declaredVendorCategories` / `profile.declaredCchCount` are
// set (post-preflight), completion is gated on those declared counts:
//   - Vendors tab: every declared category must have ≥1 vendor row.
//   - CCH tab: cch rows ≥ declaredCchCount.
// When the declared values are blank we fall back to the older "≥1 row"
// heuristic so legacy schools keep working without forcing them through
// the preflight.
// Server section keys map 1:1 to tab keys. When the backend `/api/setup-status`
// response is available, callers can pass it as `serverSections` and we'll use
// that — it consults the admin's `field_config` so any field marked mandatory
// there gates completion (iter 123 fix).
const SERVER_KEY_TO_TAB = {
  schoolIdentity: "schools",
  vendors: "vendors",
  cchs: "cchs",
  enrollment: "enrollment",
};

export function computeTabStatus({ profile, profiles, vendors, cchs, enrolls, serverSections }) {
  // When backend supplied authoritative section completion (it knows about
  // admin-toggled mandatory fields), trust it over the local heuristic.
  if (serverSections && typeof serverSections === "object") {
    return {
      schools: !!serverSections.schoolIdentity,
      vendors: !!serverSections.vendors,
      cchs: !!serverSections.cchs,
      enrollment: !!serverSections.enrollment,
    };
  }
  // Tolerate older `profiles` array signature.
  const p = profile || (profiles && profiles[0]) || null;
  // Schools tab complete when key address fields are filled.
  const schoolsDone = !!(
    p &&
    String(p.schoolName || "").trim() &&
    String(p.udiseCode || "").trim() &&
    String(p.district || "").trim() &&
    String(p.block || "").trim() &&
    String(p.village || "").trim() &&
    String(p.post || "").trim() &&
    String(p.pincode || "").trim()
  );
  const declaredCats = Array.isArray(p?.declaredVendorCategories) ? p.declaredVendorCategories : [];
  const declaredCchCount = Number.isFinite(p?.declaredCchCount) ? p.declaredCchCount : 0;
  // Vendor completion: every declared category needs at least one matching
  // vendor row. When no declaration exists, fall back to "any vendor".
  let vendorsDone;
  if (declaredCats.length > 0) {
    vendorsDone = declaredCats.every(
      (cat) => (vendors || []).some((v) => (v.category || "").toLowerCase() === cat),
    );
  } else {
    vendorsDone = Array.isArray(vendors) && vendors.length > 0;
  }
  // CCH completion: actual rows >= declared count when declared.
  let cchsDone;
  if (declaredCchCount > 0) {
    cchsDone = Array.isArray(cchs) && cchs.length >= declaredCchCount;
  } else {
    cchsDone = Array.isArray(cchs) && cchs.length > 0;
  }
  // Enrolment is optional but considered complete once at least one row exists
  // (or the boolean `true` is passed for legacy callers).
  const enrollDone = enrolls === true || (Array.isArray(enrolls) && enrolls.length > 0);
  return {
    schools: schoolsDone,
    vendors: vendorsDone,
    cchs: cchsDone,
    enrollment: enrollDone,
  };
}

// Detailed pending-reason text per tab, for the guidance banner. Returns
// an Odia/English string explaining EXACTLY what's missing.
//
// When `serverMissingBySection` is provided (from /api/setup-status) we return
// the exact field-level messages the backend computed — these honour the
// admin's dynamic `field_config` so they line up perfectly with the home
// dashboard's IncompleteSetupBanner (iter 123 fix).
export function pendingReason(tab, { profile, vendors, cchs, serverMissingBySection }) {
  if (serverMissingBySection && typeof serverMissingBySection === "object") {
    const tabToServerKey = {
      schools: "schoolIdentity",
      vendors: "vendors",
      cchs: "cchs",
      enrollment: "enrollment",
    };
    const list = serverMissingBySection[tabToServerKey[tab]] || [];
    if (list.length === 0) return "";
    // Cap the surfaced text so the red callout doesn't dominate the screen.
    const head = list.slice(0, 4);
    const more = list.length - head.length;
    const tail = more > 0 ? ` … +${more} more` : "";
    return head.join(" · ") + tail;
  }
  if (tab === "schools") {
    if (!profile) return "ବିଦ୍ୟାଳୟ profile create କରନ୍ତୁ।";
    const missing = [];
    if (!String(profile.schoolName || "").trim()) missing.push("School Name");
    if (!String(profile.udiseCode || "").trim()) missing.push("UDISE Code");
    if (!String(profile.district || "").trim()) missing.push("District");
    if (!String(profile.block || "").trim()) missing.push("Block");
    if (!String(profile.village || "").trim()) missing.push("Village");
    if (!String(profile.post || "").trim()) missing.push("Post");
    if (!String(profile.pincode || "").trim()) missing.push("Pincode");
    if (missing.length === 0) return "";
    return `ଏହି field ଗୁଡ଼ିକ ଖାଲି ଅଛି: ${missing.join(", ")}`;
  }
  if (tab === "vendors") {
    const declared = Array.isArray(profile?.declaredVendorCategories) ? profile.declaredVendorCategories : [];
    if (declared.length === 0) {
      return (vendors || []).length === 0
        ? "କୌଣସି Vendor entry ନାହିଁ — ଅନ୍ତତଃ ଗୋଟିଏ Vendor ଯୋଡନ୍ତୁ।"
        : "";
    }
    const missing = declared.filter(
      (c) => !(vendors || []).some((v) => (v.category || "").toLowerCase() === c),
    );
    if (missing.length === 0) return "";
    const labels = missing.map((c) => VENDOR_CATEGORY_LABELS_ODIA[c] || c);
    return `ଏହି category ର Vendor ଯୋଗ କରନ୍ତୁ: ${labels.join(", ")}`;
  }
  if (tab === "cchs") {
    const declared = Number.isFinite(profile?.declaredCchCount) ? profile.declaredCchCount : 0;
    const have = (cchs || []).length;
    if (declared > 0 && have < declared) {
      return `ଆପଣ ${declared} CCH declare କରିଛନ୍ତି — ବର୍ତ୍ତମାନ ${have} entry ଅଛି। ଆଉ ${declared - have} CCH ଯୋଡନ୍ତୁ।`;
    }
    if (declared === 0 && have === 0) {
      return "କୌଣସି CCH entry ନାହିଁ — ଅନ୍ତତଃ ଗୋଟିଏ CCH ଯୋଡନ୍ତୁ।";
    }
    return "";
  }
  if (tab === "enrollment") {
    return "ଅନ୍ତତଃ ଗୋଟିଏ Academic Year ର ନାମଲେଖା save କରନ୍ତୁ।";
  }
  return "";
}

// Which tab should we suggest the user open next, given current status?
// Returns the FIRST tab in SETUP_TABS order that's still pending. Returns
// null when everything is done.
export function nextPendingTab(status) {
  for (const t of SETUP_TABS) {
    if (!status[t]) return t;
  }
  return null;
}

// Categories the "Apply to all vendor categories" shortcut will broadcast to.
// Must mirror VENDOR_CATEGORIES in Setup.jsx.
export const ALL_VENDOR_CATEGORIES = ["grocery", "vegetable", "egg", "fuel", "ladoo", "other"];
export const VENDOR_CATEGORY_LABELS_ODIA = {
  grocery: "Grocery (କିରାନା)",
  vegetable: "Vegetable (ପନିପରିବା)",
  egg: "Egg (ଅଣ୍ଡା)",
  fuel: "Fuel (ଇନ୍ଧନ)",
  ladoo: "Ladoo (ଲଡୁ)",
  other: "Other (ଅନ୍ୟ)",
};
